import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, LOCALE_ID, isDevMode, InjectionToken } from '@angular/core';
import { PreloadAllModules, provideRouter, Router, RouteReuseStrategy, TitleStrategy, withPreloading, withRouterConfig } from '@angular/router';

import { routes } from './app.routes';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { OAuthModule, OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';
import { RootStoreModule } from './store/root-store.module';
import { CldrIntlService, IntlModule, IntlService } from '@progress/kendo-angular-intl';
import { TooltipsModule } from '@progress/kendo-angular-tooltip';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { ExcelExportModule } from '@progress/kendo-angular-excel-export';
import { MenuModule } from '@progress/kendo-angular-menu';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { ListViewModule } from '@progress/kendo-angular-listview';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AuthInterceptor } from './core/interceptors/auth/auth.interceptor';
import { ApiInterceptor } from './core/interceptors/api/api.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { DialogModule } from '@angular/cdk/dialog';
import '@angular/common/locales/global/en';
import '@angular/common/locales/global/nl';
import '@angular/common/locales/global/fr';
import '@angular/common/locales/global/sv';
import '@angular/common/locales/global/lt';
import '@angular/common/locales/global/de';
import '@angular/common/locales/global/pt';
import '@angular/common/locales/global/it';
import '@angular/common/locales/global/es';
import '@angular/common/locales/global/pl';
import '@progress/kendo-angular-intl/locales/bg/all';
import '@progress/kendo-angular-intl/locales/en/all';
import '@progress/kendo-angular-intl/locales/nl/all';
import '@progress/kendo-angular-intl/locales/fr/all';
import '@progress/kendo-angular-intl/locales/sv/all';
import '@progress/kendo-angular-intl/locales/lt/all';
import '@progress/kendo-angular-intl/locales/de/all';
import '@progress/kendo-angular-intl/locales/pt/all';
import '@progress/kendo-angular-intl/locales/it/all';
import '@progress/kendo-angular-intl/locales/es/all';
import '@progress/kendo-angular-intl/locales/pl/all';
import * as Sentry from '@sentry/angular';
import 'hammerjs';
import { BizzMineSessionStorageService } from './shared/services/localStorage/bizzmine-session-storage.service';
import { BizzTitleStrategy } from './bizz-title-strategy';
import { MessageService } from '@progress/kendo-angular-l10n';
import { MessageTranslationService } from './core/services/kendo-translations/message-translation.service';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import { BizzRouteReuseStrategy } from './core/classes/route-reuse-strategy';
import { AppSettingsService } from './core/services/app-settings/app-settings.service';
import { BizzMineLocalStorageService } from './shared/services/localStorage/bizzmine-local-storage.service';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { BizzIntlService } from './core/services/localization/first-day-intl.service';

export function portalUrlFactory(
  router: Router,
  localStoragetest: BizzMineLocalStorageService){
  return () => {
    const bizzminePortalUrl = localStoragetest.getItem('bizzminePortalUrlPath');
    if (bizzminePortalUrl != null) {
    return router.navigate([bizzminePortalUrl]);
    }
    return Promise.resolve();
  }
}
export function appInitializerFactory(
  appSettingsService: AppSettingsService
) {
  return () => {
    return appSettingsService.configureApplicationSettings();
  }
}
export const APP_CONFIG: ApplicationConfig = {
  providers: [
    provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'always', onSameUrlNavigation: 'reload' })),
    { provide: RouteReuseStrategy, useClass: BizzRouteReuseStrategy},
    importProvidersFrom(BrowserModule, DialogModule, OAuthModule.forRoot(),
      RootStoreModule, IntlModule, HammerModule, TooltipsModule, DateInputsModule, ExcelExportModule, MenuModule, NgScrollbarModule, NgxExtendedPdfViewerModule ,  ListViewModule),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'en-GB' },
    { provide: CldrIntlService, useClass: CldrIntlService },
    { provide: IntlService, useClass: BizzIntlService },
    {
      provide: OAuthStorage,
      useFactory: (bizzMineSessionStorageService: BizzMineSessionStorageService) => bizzMineSessionStorageService,
      deps: [BizzMineSessionStorageService]
    },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideOAuthClient(),
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }) },
    { provide: MessageService, useClass: MessageTranslationService },
    { provide: Sentry.TraceService, deps: [Router] },
    {
      provide: APP_INITIALIZER,
      useFactory: portalUrlFactory,
      deps: [Router, BizzMineLocalStorageService],
      multi: true
    },
    { 
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [AppSettingsService, Sentry.TraceService],
      multi: true
    },
    { provide: TitleStrategy, useClass: BizzTitleStrategy },
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000'
    }),
    environment.providers,
  ]
};
